/* eslint-disable react/no-unknown-property */
import React from 'react';
import { ActionIcon, ICON_CLOSE } from '@doveit/bricks';
import { Agency } from '../../../providers/api/dtos';
import { BrandTemplateAutofillAgencyData, BrandTemplateAutofillDecodedDataset } from '../../utils/autofill/types';
import { useAgent, useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import AgencyAutoComplete from '../../../agency/containers/agency-autocomplete/AgencyAutoComplete';
import { mapAgencyToAutofillAgencyData } from '../template-agent-selector/utils';
import useAgency from '../../../agency/hooks/use-agency/useAgency';
import * as styles from './TemplateAgencySelector.style';
import ViewAgencyPreview from '../../../agency/containers/view-agency-preview/ViewAgencyPreview';

export interface TemplateAgencySelectorProps {
  decodedDataset: BrandTemplateAutofillDecodedDataset,
  agencyId?: NonNullable<Agency['id']>,
  agentId?: number,
  disabled?: boolean,
  onAgencySelected?: (agency: Agency | null) => void,
  onAgencyDeselected?: VoidFunction,
  onDataChange?: (agencyData?: BrandTemplateAutofillAgencyData) => void,
}

export const TemplateAgencySelector: React.FC<TemplateAgencySelectorProps> = ({
  agencyId,
  agentId,
  disabled,
  onAgencySelected,
  onAgencyDeselected,
  onDataChange,
}) => {
  const [id, setId] = React.useState<NonNullable<Agency['id']> | undefined>();

  const { data: currentAgent } = useCurrentAgentSync();
  const { data: fetchedAgent } = useAgent(agentId);
  const agent = fetchedAgent ?? currentAgent;

  const { data: agency } = useAgency(id);
  const [agencyData, setAgencyData] = React.useState<BrandTemplateAutofillAgencyData>();

  const hasOnlyOneAgency = React.useMemo(
    () => agent?.agencies?.length === 1,
    [agent?.agencies?.length],
  );

  React.useEffect(() => {
    if (agencyId) {
      setId(agencyId);
    } else if (hasOnlyOneAgency) {
      setId(agent?.agencies?.[0]?.id);
    } else {
      setId(undefined);
    }
  }, [agencyId, hasOnlyOneAgency, agent?.agencies]);

  React.useEffect(() => {
    onDataChange?.(agencyData);
  }, [agencyData, onDataChange]);

  React.useEffect(() => {
    if (agency) {
      setAgencyData((prev) => ({
        ...(prev ?? {}),
        ...mapAgencyToAutofillAgencyData(agency),
      }));
    }
  }, [agency]);

  const clearAgencySelection = React.useCallback(() => {
    setAgencyData(undefined);
    setId(undefined);

    onAgencyDeselected?.();
  }, [onAgencyDeselected]);

  const onInternalAgencySelected = React.useCallback(
    (selectedAgency: Agency | null) => {
      setId(selectedAgency?.id);
      onAgencySelected?.(selectedAgency);
    },
    [onAgencySelected],
  );

  return (
    <>
      {!id && (
        <div css={styles.autocompleteWrapper}>
          <AgencyAutoComplete
            aria-label="Seleziona un'agenzia"
            placeholder="Cerca agenzia"
            onSuggestionSelected={onInternalAgencySelected}
            maxSuggestions={10}
            agentEmails={agent?.email.work}
          />
        </div>
      )}

      {id && (
        <div>
          <div css={styles.previewWrapper}>
            <ViewAgencyPreview
              agencyId={id}
              primaryActions={[
                <ActionIcon
                  icon={{ path: ICON_CLOSE }}
                  label="Deseleziona l'agenzia"
                  aria-label="Annulla la selezione dell'agenzia"
                  disabled={disabled}
                  onClick={clearAgencySelection}
                  size="S"
                />,
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
};
